var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { quillEditor } from 'vue-quill-editor';
import Quill from 'quill';
import QuillBetterTable from 'quill-better-table';
import QuillResize from 'quill-resize-module';
import i18n from '@/i18n';
import 'quill/dist/quill.bubble.css';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import { addTableModule, getCurrentInsertionIndex, populateWeekOverviewTable, registerCustomButtons } from '@/support/AQuillEditorSupport';
// Register extra Quill modules
Quill.register('modules/better-table', QuillBetterTable);
Quill.register('modules/QuillResize', QuillResize);
let AQuillEditor = class AQuillEditor extends Vue {
    constructor() {
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.isEditorReady = false;
        this.internalValue = '';
        this.internalEditor = null;
        this.studentDropdownVisible = false;
        this.dropdownPosition = { top: 0, left: 0 };
        this.currentSelection = null;
        this.selectedStudentIds = [];
        this.studentDropdownSelection = null;
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks
    mounted() {
        this.initialize();
    }
    async initialize() {
        await this.$nextTick();
        this.setupEditor();
    }
    beforeDestroy() {
        this.isEditorReady = false;
        this.internalEditor = null;
    }
    // #endregion
    // #region Event handlers
    onEditorReady(editor) {
        this.internalEditor = editor;
        this.isEditorReady = true;
        if (this.value) {
            this.internalValue = this.value;
            editor.root.innerHTML = this.value;
        }
        editor.on('selection-change', (range) => {
            const selection = range || {
                index: Math.max(0, editor.getLength() - 1),
                length: 0,
            };
            this.currentSelection = selection;
        });
        editor.on('text-change', (delta, oldDelta, source) => {
            if (source === 'user' || source === 'api') {
                this.internalValue = editor.root.innerHTML;
                this.$emit('input', this.internalValue);
                const selection = editor.getSelection();
                if (selection) {
                    this.currentSelection = { ...selection };
                }
            }
        });
        this.initializeInitialSelection(editor);
    }
    onDropdownClickOutside() {
        this.studentDropdownVisible = false;
        if (this.studentDropdownSelection) {
            const endIndex = this.studentDropdownSelection.index + this.studentDropdownSelection.length;
            this.currentSelection = { index: endIndex, length: 0 };
            if (this.internalEditor) {
                this.internalEditor.setSelection(endIndex, 0, 'api');
            }
        }
        else {
            this.currentSelection = null;
        }
        this.selectedStudentIds = [];
        this.studentDropdownSelection = null;
    }
    onStudentSelected(studentIds) {
        if (!this.internalEditor)
            return;
        this.selectedStudentIds = studentIds;
        const selection = this.studentDropdownSelection;
        if (!selection)
            return;
        if (!studentIds || studentIds.length === 0) {
            this.internalEditor.deleteText(selection.index, selection.length, 'api');
            this.currentSelection = { index: selection.index, length: 0 };
            this.studentDropdownSelection = { index: selection.index, length: 0 };
            return;
        }
        const students = this.studentList.filter((student) => studentIds.includes(student.id)).map((student) => student.name);
        const text = students.join(', ');
        this.internalEditor.deleteText(selection.index, selection.length, 'api');
        this.internalEditor.insertText(selection.index, text, 'api');
        const newIndex = selection.index + text.length;
        this.internalEditor.setSelection(newIndex, 0, 'api');
        this.currentSelection = { index: newIndex, length: 0 };
        this.studentDropdownSelection = { index: selection.index, length: text.length };
    }
    // #endregion
    // #region Class methods
    setupEditor() {
        if (!this.internalEditor)
            return;
        addTableModule(this.internalEditor);
        registerCustomButtons(this.internalEditor, this.customActions);
    }
    initializeInitialSelection(editor) {
        const initialSelection = editor.getSelection();
        if (initialSelection) {
            this.currentSelection = initialSelection;
        }
        else {
            const length = editor.getLength();
            this.currentSelection = { index: Math.max(0, length - 1), length: 0 };
        }
    }
    async insertWeekOverviewTable() {
        if (!this.internalEditor)
            return;
        const tableModule = this.internalEditor.getModule('better-table');
        if (tableModule) {
            const insertionIndex = getCurrentInsertionIndex(this.internalEditor);
            const tableRows = 5; // Default number of rows
            const tableColumns = 2; // Default number of columns
            tableModule.insertTable(tableRows, tableColumns);
            await this.$nextTick();
            populateWeekOverviewTable(this.internalEditor, insertionIndex);
        }
        else {
            console.error('Table module not found');
        }
    }
    showStudentDropdown(editor, data, event) {
        if (!editor || !data || !event)
            return;
        const button = event.target;
        const rect = button.getBoundingClientRect();
        this.studentDropdownVisible = true;
        this.dropdownPosition = {
            top: rect.bottom + 5,
            left: rect.left - 0,
        };
        this.studentDropdownSelection = this.currentSelection ? { ...this.currentSelection } : { index: 0, length: 0 };
    }
    // #endregion
    // #region Async methods (api calls only)
    // #endregion
    // #region Getters & Setters
    get customActions() {
        const actions = [
            {
                name: AQuillEditorActionsEnum.WEEK_OVERVIEW,
                icon: '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="12" x="3" y="3"/><circle class="ql-fill" cx="6" cy="6" r="1"/><circle class="ql-fill" cx="9" cy="6" r="1"/><circle class="ql-fill" cx="12" cy="6" r="1"/><circle class="ql-fill" cx="6" cy="9" r="1"/><circle class="ql-fill" cx="9" cy="9" r="1"/><circle class="ql-fill" cx="12" cy="9" r="1"/><circle class="ql-fill" cx="6" cy="12" r="1"/><circle class="ql-fill" cx="9" cy="12" r="1"/><circle class="ql-fill" cx="12" cy="12" r="1"/><path class="ql-stroke" d="m5 2v4m6-4v4"/></svg>',
                handler: this.insertWeekOverviewTable,
                visible: true,
                data: null,
            },
            {
                name: AQuillEditorActionsEnum.STUDENT_LIST,
                icon: '<svg viewBox="0 0 18 18"><line class="ql-stroke" x1="6" y1="4" x2="15" y2="4"/><line class="ql-stroke" x1="6" y1="9" x2="15" y2="9"/><line class="ql-stroke" x1="6" y1="14" x2="15" y2="14"/><line class="ql-stroke" x1="3" y1="4" x2="3" y2="4" stroke-width="2"/><line class="ql-stroke" x1="3" y1="9" x2="3" y2="9" stroke-width="2"/><line class="ql-stroke" x1="3" y1="14" x2="3" y2="14" stroke-width="2"/></svg>',
                handler: this.showStudentDropdown,
                visible: this.studentList && this.studentList.length > 0,
                data: this.studentList,
            },
        ];
        return actions.filter((action) => action.visible && !this.disabledActions.includes(action.name));
    }
    get mergedEditorOptions() {
        const customToolbarItems = this.customActions.map((action) => ({ [action.name]: action.name }));
        const defaultToolbar = [[{ header: [1, 2, 3, false] }], ['bold', 'italic', 'underline', 'link'], [{ list: 'ordered' }, { list: 'bullet' }], [{ align: '' }, { align: 'center' }, { align: 'right' }], ['clean'], ['image', 'table', ...customToolbarItems]];
        const filteredToolbar = defaultToolbar.map((group) => {
            return Array.isArray(group)
                ? group.filter((item) => {
                    if (typeof item === 'string') {
                        return !this.disabledActions.includes(item);
                    }
                    if (typeof item === 'object' && !Array.isArray(item)) {
                        const key = Object.keys(item)[0];
                        if (key === 'list' && this.disabledActions.includes(AQuillEditorActionsEnum.LIST)) {
                            return false;
                        }
                        return !this.disabledActions.includes(key);
                    }
                    return true;
                })
                : group;
        });
        const defaultOptions = {
            placeholder: i18n.tc('Enter text here...'),
            modules: {
                'magicUrl': true,
                'toolbar': filteredToolbar,
                'table': false,
                'better-table': {
                    operationMenu: {
                        items: {
                            insertColumnRight: {
                                text: this.$tc('Insert column right'),
                            },
                            insertColumnLeft: {
                                text: this.$tc('Insert column left'),
                            },
                            insertRowUp: {
                                text: this.$tc('Insert row up'),
                            },
                            insertRowDown: {
                                text: this.$tc('Insert row down'),
                            },
                            mergeCells: false,
                            unmergeCells: false,
                            deleteColumn: {
                                text: this.$tc('Delete column'),
                            },
                            deleteRow: {
                                text: this.$tc('Delete row'),
                            },
                            deleteTable: false,
                        },
                    },
                },
                'resize': {
                    modules: ['Resize', 'DisplaySize'],
                },
            },
        };
        return {
            ...defaultOptions,
            ...this.editorOptions,
            modules: {
                ...defaultOptions.modules,
                ...this.editorOptions.modules,
            },
        };
    }
    // #endregion
    // #region @Watchers
    onValueChange(newValue) {
        if (this.internalEditor && newValue !== this.internalValue) {
            this.internalValue = newValue;
            this.internalEditor.root.innerHTML = newValue || '';
        }
    }
};
__decorate([
    Prop({
        default: () => ({}),
    }),
    __metadata("design:type", typeof (_a = typeof Partial !== "undefined" && Partial) === "function" ? _a : Object)
], AQuillEditor.prototype, "editorOptions", void 0);
__decorate([
    Prop({ default: () => [] }),
    __metadata("design:type", Array)
], AQuillEditor.prototype, "disabledActions", void 0);
__decorate([
    Prop({ required: false }),
    __metadata("design:type", Array)
], AQuillEditor.prototype, "studentList", void 0);
__decorate([
    Prop({ default: '' }),
    __metadata("design:type", String)
], AQuillEditor.prototype, "value", void 0);
__decorate([
    Watch('value', { immediate: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], AQuillEditor.prototype, "onValueChange", null);
AQuillEditor = __decorate([
    Component({
        components: {
            quillEditor,
        },
    })
], AQuillEditor);
export default AQuillEditor;
// #region Enums
export var AQuillEditorActionsEnum;
(function (AQuillEditorActionsEnum) {
    AQuillEditorActionsEnum["MAGIC_URL"] = "magicUrl";
    AQuillEditorActionsEnum["TABLE"] = "table";
    AQuillEditorActionsEnum["IMAGE"] = "image";
    AQuillEditorActionsEnum["LIST"] = "list";
    AQuillEditorActionsEnum["BOLD"] = "bold";
    AQuillEditorActionsEnum["ITALIC"] = "italic";
    AQuillEditorActionsEnum["UNDERLINE"] = "underline";
    AQuillEditorActionsEnum["LINK"] = "link";
    AQuillEditorActionsEnum["HEADER"] = "header";
    AQuillEditorActionsEnum["ALIGN"] = "align";
    AQuillEditorActionsEnum["CLEAN"] = "clean";
    AQuillEditorActionsEnum["WEEK_OVERVIEW"] = "week-overview";
    AQuillEditorActionsEnum["STUDENT_LIST"] = "student-list";
})(AQuillEditorActionsEnum || (AQuillEditorActionsEnum = {}));
// #endregion
// #region Constants
// #endregion
